import { PlusIcon } from 'lucide-react';
import { ReactNode, useMemo, useState } from 'react';

import { SkuType, type SkuDTO } from '@invenco/products-interface';

import { Button } from '@/components/core/button';
import { Card, CardFooter, CardHeader } from '@/components/core/card';
import { EditableDataTable, useEditableRowDataTable } from '@/components/data-table';
import { DataTableCardBody, DataTableCardTitle } from '@/components/data-table/data-table-card';
import { SkuCombobox } from '@/pages/seller/components/sku-combobox';
import { Result } from '@/shared/helpers/Result';

import {
  asnLineEditableSchema,
  asnLineRecostEditableSchema,
  calculateUnitCostTotal,
  calculateUnitPurchaseCost,
  getAsnLineColumns,
  type AsnLine,
  type AsnLineAddInput,
  type AsnLineEditableValues,
  type AsnLineRecostEditableValues,
  type AsnLineRow,
  type AsnLineUpdatePatch,
} from './asn-lines-card-columns';

const DRAFT_ASN_LINE_ID = 'new-asn-line';

type EditableAsnLinesCardProps = {
  title: string;
  lines: AsnLine[];
  isLoading?: boolean;
  isLoadingWithStaleData?: boolean;
  hasError?: boolean;
  showQtyReceived?: boolean;
  isRecosting?: boolean;
  emptyMessage?: ReactNode;
  onAddLine?: (line: AsnLineAddInput) => Promise<void | Result<unknown>>;
  onUpdateLine: (lineId: string, patch: AsnLineUpdatePatch) => Promise<void | Result<unknown>>;
  onDeleteLine?: (lineId: string) => Promise<void | Result<unknown>>;
};

function buildDraftLine(id: string, sku: SkuDTO): AsnLineRow {
  const line = {
    id,
    skuId: sku.id,
    skuName: sku.name,
    description: sku.description ?? '',
    unitPurchaseCost: sku.purchasePrice ?? 0,
    unitCostTotal: 0,
  };

  return { ...line, unitCostTotal: calculateUnitCostTotal(line) };
}

function buildAddPayload(line: AsnLineRow, values: AsnLineEditableValues): AsnLineAddInput {
  return {
    skuId: line.skuId,
    skuName: line.skuName,
    description: values.description,
    qtyExpected: values.qtyExpected,
    unitPurchaseCost: values.unitPurchaseCost,
    unitCostTotal: values.unitCostTotal,
  };
}

function buildUpdatePayload(
  values: AsnLineEditableValues | AsnLineRecostEditableValues,
): AsnLineUpdatePatch {
  return {
    ...('description' in values ? { description: values.description } : {}),
    ...('qtyExpected' in values ? { qtyExpected: values.qtyExpected } : {}),
    unitPurchaseCost: values.unitPurchaseCost,
    unitCostTotal: values.unitCostTotal,
  };
}

export function EditableAsnLinesCard({
  title,
  lines,
  isLoading,
  isLoadingWithStaleData,
  hasError,
  showQtyReceived,
  isRecosting,
  emptyMessage = 'No items found',
  onAddLine,
  onUpdateLine,
  onDeleteLine,
}: EditableAsnLinesCardProps) {
  const [isAdding, setIsAdding] = useState(false);
  const [draftLine, setDraftLine] = useState<AsnLineRow | null>(null);

  const renderedLines = useMemo<AsnLineRow[]>(
    () => (draftLine && onAddLine ? [...lines, draftLine] : lines),
    [draftLine, lines, onAddLine],
  );
  const shouldShowSkuCombobox = isAdding || renderedLines.length === 0;
  const columns = useMemo(
    () => getAsnLineColumns({ showQtyReceived, isRecosting }),
    [isRecosting, showQtyReceived],
  );
  const schema = isRecosting ? asnLineRecostEditableSchema : asnLineEditableSchema;

  const {
    table,
    editingRowId,
    startEditingRow,
    onClickRow,
    onCancelEdit,
    ...editableDataTableProps
  } = useEditableRowDataTable({
    data: renderedLines,
    columns,
    schema,
    isLoading,
    isLoadingWithStaleData,
    hasError,
    // This card has no pagination controls, so opt out of client pagination — otherwise
    // TanStack's default 10-row page size silently hides rows (including the draft row).
    manualPagination: true,
    manualSorting: false,
    onSaveRow: async (line, values) => {
      if (draftLine?.id === line.id) {
        if (!onAddLine) return undefined;

        const addValuesResult = asnLineEditableSchema.safeParse(values);
        if (!addValuesResult.success) throw new Error('Missing ASN line values for add');

        const result = await onAddLine(buildAddPayload(line, addValuesResult.data));
        if (result?.isFailure) return result;
        setDraftLine(null);
        return result;
      }

      return onUpdateLine(line.id, buildUpdatePayload(values));
    },
    onStagedRowChange: ({ changedColumnId, draft, setValues }) => {
      switch (changedColumnId) {
        case 'qtyExpected':
          setValues({ unitCostTotal: calculateUnitCostTotal(draft) });
          break;
        case 'unitPurchaseCost':
          setValues({
            unitCostTotal:
              draft.unitPurchaseCost === null || draft.unitPurchaseCost === undefined
                ? null
                : calculateUnitCostTotal(draft),
          });
          break;
        case 'unitCostTotal':
          setValues({
            unitPurchaseCost:
              draft.unitCostTotal === null || draft.unitCostTotal === undefined
                ? null
                : calculateUnitPurchaseCost(draft),
          });
          break;
      }
    },
    onDeleteRow: onDeleteLine
      ? async (line) => {
          if (draftLine?.id === line.id) {
            setDraftLine(null);
            return undefined;
          }

          return onDeleteLine(line.id);
        }
      : undefined,
  });

  const handleClickRow: typeof onClickRow = (row, event) => {
    setIsAdding(false);

    if (draftLine?.id === editingRowId && row.id !== draftLine.id) {
      setDraftLine(null);
    }

    onClickRow(row, event);
  };

  function handleSelectSku(sku: SkuDTO) {
    const nextDraftLine = buildDraftLine(DRAFT_ASN_LINE_ID, sku);

    setDraftLine(nextDraftLine);
    setIsAdding(false);
    startEditingRow(nextDraftLine.id, nextDraftLine, { columnId: 'qtyExpected' });
  }

  function handleClickAdd() {
    if (editingRowId) {
      if (draftLine?.id === editingRowId) {
        setDraftLine(null);
      }

      onCancelEdit();
    }

    setIsAdding(true);
  }

  function handleCancelEdit() {
    if (draftLine && editingRowId === draftLine.id) {
      setDraftLine(null);
      setIsAdding(false);
    }

    onCancelEdit();
  }

  return (
    <Card aria-label={title}>
      <CardHeader>
        <DataTableCardTitle table={table}>{title}</DataTableCardTitle>
      </CardHeader>
      <DataTableCardBody table={table} emptyMessage={emptyMessage}>
        <EditableDataTable
          table={table}
          editingRowId={editingRowId}
          {...editableDataTableProps}
          onClickRow={handleClickRow}
          onCancelEdit={handleCancelEdit}
          submitLabel={isRecosting ? 'Recost' : undefined}
        />
      </DataTableCardBody>
      {onAddLine ? (
        <CardFooter className="flex h-10 items-stretch bg-card p-0">
          {shouldShowSkuCombobox ? (
            <SkuCombobox
              className="h-full w-full rounded-none border-0 px-2 shadow-none has-[[data-slot=input-group-control]:focus-visible]:border-0 has-[[data-slot=input-group-control]:focus-visible]:ring-0"
              queryParams={{ archived: false, type: SkuType.STANDARD }}
              onSelect={handleSelectSku}
              onCancel={() => setIsAdding(false)}
            />
          ) : (
            <Button
              type="button"
              variant="outline"
              className="h-full w-full justify-start rounded-none border-0 px-4 shadow-none"
              onClick={handleClickAdd}
            >
              <PlusIcon />
              Add new item
            </Button>
          )}
        </CardFooter>
      ) : null}
    </Card>
  );
}
